/**
 * Signup module.
 */

import { isCookiePolicyAccepted, readCookie } from 'scripts/modules/_cookies';
import { getFieldValue } from 'scripts/modules/_forms';
import {
	getUserCountryName,
	getUserCurrencyCode,
	getSiteCountryCode,
	getLanguage,
	setUserCurrencyCodeCookie,
} from 'scripts/modules/_i18n';
import { getAttrOrData } from 'scripts/helpers/_attributes';
import { getVisitorId, track, trackEvent } from 'scripts/modules/_tracking';
import 'bootstrap/js/dist/modal';
import loaderImage from 'images/signup/loader.svg';
import { v4 as uuidv4 } from 'uuid';

/**
 * Set parameters for a signup request, and process the response.
 *
 * @param {Object} form Form object which is being submitted from.
 */
export const handleSignup = ( form ) => {
	if ( signupCookiesAccepted() ) {
		Array.from( form.querySelectorAll( 'input' ) ).forEach(
			( input ) => ( input.disabled = true )
		);
		Array.from( form.querySelectorAll( 'button' ) ).forEach(
			( input ) => ( input.disabled = true )
		);
		Array.from( form.querySelectorAll( 'a.btn-outline-grey' ) ).forEach( ( input ) =>
			input.classList.add( 'disabled' )
		);
		const submitButton = form.querySelector( '[type="submit"]' );
		const originalSubmitText = submitButton.innerHTML;
		const loaderNode = document.createElement( 'img' );
		loaderNode.src = loaderImage;
		submitButton.innerHTML = loaderNode.outerHTML;

		const action = getAttrOrData( form, 'action' );
		let referralId = null;
		if ( readCookie( 'fb_landing_url' ) !== null ) {
			const referralCookie = decodeURIComponent( readCookie( 'fb_landing_url' ) ).match(
				'[?&]ref=([^&#]*)'
			);

			if ( referralCookie && referralCookie.length ) {
				[ , referralId ] = referralCookie;
			}
		}

		const profilingSessionId = uuidv4();
		if ( typeof window?.profiling?.send === 'function' ) {
			window.profiling.send( 'secure.freshbooks.com', '32rts1pk', profilingSessionId );
		}

		// @TODO: Remove once MAP-7799 is finished.
		const checkoutFlow = document.querySelector( '.checkout-flow' );
		let checkoutFlowData = {};
		if ( checkoutFlow ) {
			checkoutFlowData = {
				checkoutFlow: true,
				plan: checkoutFlow.values.package,
				teamMembers: checkoutFlow.values.addons[ 'team-members' ],
				advancedPaymentsEnabled: checkoutFlow.values.addons[ 'advanced-payments' ],
			};
		}

		const formData = {
			id: getFieldValue( form.email ),
			email: getFieldValue( form.email ),
			password: getFieldValue( form.password ),
			landing_url: decodeURIComponent( readCookie( 'fb_landing_url' ) ),
			provisioner: 'magnum',
			send_confirmation_notification: true,
			visitor_id: getVisitorId(),
			access_token: null,
			capacity: null,
			billing_country_code: getSiteCountryCode(),
			country: getUserCountryName(),
			currencyCode: getUserCurrencyCode(),
			language: getLanguage(),
			referralid: referralId,
			referring_url: decodeURIComponent( readCookie( 'fb_referring_url' ) ),
			web_promo: readCookie( 'fb_web_promo' ),
			skip_business: false,
			skip_system: false,
			profiling_session_id: profilingSessionId,
			...checkoutFlowData,
		};

		window
			.fetch( action, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify( formData ),
			} )
			.then( ( response ) => response.json() )
			.then( ( registerResponse ) => {
				if ( registerResponse.response ) {
					const signupRedirect = `https://${
						window.fbVars.fbDomains.auth
					}/integrations/confirm?email=${ encodeURIComponent( formData.email ) }&firstVisit=true`;

					window
						.fetch( `https://${ window.fbVars.fbDomains.api }/auth/api/v1/users/me`, {
							method: 'GET',
							headers: {
								Authorization: `Bearer ${ registerResponse.response.access_token }`,
							},
						} )
						.then( ( response ) => response.json() )
						.then( ( apiResponse ) => {
							// Separate GTM push for accountid so it's available to later pushes.
							if ( apiResponse && apiResponse.account_id ) {
								track( 'gtm', { accountid: apiResponse.account_id } );
							}

							track( 'gtm', {
								classic_or_nfb: 'nfb',
								event: 'smux.account.signup',
							} );

							if ( [ 'au', 'ca', 'gb', 'us' ].includes( window.fbVars.i18n.siteCountryCode ) ) {
								window
									.fetch( 'https://www2.freshbooks.com/l/490351/2024-02-14/3gbkcf5', {
										method: 'POST',
										credentials: 'include',
										headers: {
											'Content-Type': 'application/x-www-form-urlencoded',
										},
										body: new URLSearchParams( {
											event: 'signup',
											...formData,
											password: true,
											visitor_id490351: readCookie( 'visitor_id490351' ) || 'undefined',
										} ).toString(),
									} )
									.finally( () => {
										window.location = signupRedirect;
									} );
							} else {
								window.location = signupRedirect;
							}
						} );
				} else if ( registerResponse.error_description ) {
					const emailField = form.email;
					const emailTooltip = form.querySelector( 'input[name="email"] ~ .invalid-tooltip' );

					let errorMessage = registerResponse.error_description.replace(
						'Validation failed: ',
						''
					);

					if ( registerResponse.error === 'unprocessable_entity' ) {
						errorMessage = 'This field is invalid';
					}

					Array.from( form.querySelectorAll( 'input' ) ).forEach(
						( input ) => ( input.disabled = false )
					);
					Array.from( form.querySelectorAll( 'button' ) ).forEach(
						( input ) => ( input.disabled = false )
					);
					Array.from( form.querySelectorAll( 'a.btn-outline-grey' ) ).forEach( ( input ) =>
						input.classList.remove( 'disabled' )
					);
					submitButton.innerHTML = originalSubmitText;

					emailTooltip.innerHTML = errorMessage;
					emailField.setCustomValidity( errorMessage );
					form.classList.add( 'was-validated' );
					emailField.focus();
				}
			} );
	}
};

/**
 * Signup with SSO provider. If provider string indicated, will supply the url based on the provider.
 * Otherwise, provider URL will be based off the SSO button class.
 *
 * @param {string} provider SSO Provider as a string ("google" or "apple").
 */
export const handleSsoSignup = ( provider ) => {
	if ( 'google' === provider || 'apple' === provider ) {
		const authEndpoint = provider === 'google' ? 'google_oauth2_central_sso' : provider;
		const signupButtons = document.querySelectorAll( `.sso-signup_${ provider }` );
		const tosCheckbox = document.querySelector( '#tos-accepted' );

		if ( signupButtons ) {
			signupButtons.forEach( ( signupButton ) => {
				signupButton.addEventListener( 'click', ( e ) => {
					e.preventDefault();
					if ( signupCookiesAccepted() ) {
						const href = signupButton.getAttribute( 'href' );
						const currencyCode = getUserCurrencyCode();
						setUserCurrencyCodeCookie( currencyCode );
						if ( ( tosCheckbox && tosCheckbox.checked ) || ! tosCheckbox ) {
							trackEvent( 'gtm', `${ provider }sso.signup.triggered` );
							if ( href && href.length > 1 && href !== '#' ) {
								window.location.href = signupButton.href;
							} else {
								window.location.href = `https://${ window.fbVars.fbDomains.auth }/service/auth/auth/${ authEndpoint }?intent=sign_up&currency_code=${ currencyCode }`;
							}
						}
					}
				} );
			} );
		}
	}
};

/**
 * Checks the cookie policy prior to signup.
 */
export const signupCookiesAccepted = () => {
	const tosCheckbox = document.querySelector( '#tos-accepted' );
	if ( isCookiePolicyAccepted() || tosCheckbox.checked ) {
		return true;
	}

	return false;
};
